import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ShieldCheck } from "lucide-react";

const ForcePasswordChange = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess.session?.user.id;
      if (!uid || !mounted) return;
      setUserId(uid);
      const { data: prof } = await supabase
        .from("profiles")
        .select("must_change_password, first_login_at")
        .eq("id", uid)
        .maybeSingle();
      if (!mounted) return;
      if (prof?.must_change_password) setOpen(true);
      // Stamp first_login_at if missing
      if (prof && !prof.first_login_at) {
        await supabase.from("profiles").update({ first_login_at: new Date().toISOString() }).eq("id", uid);
        if (!prof.must_change_password) setShowWelcome(true);
      }
      // Update last_login_at always
      await supabase.from("profiles").update({ last_login_at: new Date().toISOString() }).eq("id", uid);
    };
    check();
    return () => { mounted = false; };
  }, []);

  const submit = async () => {
    if (pw.length < 8) { toast({ variant: "destructive", title: "Password too short", description: "Use 8+ characters" }); return; }
    if (pw !== pw2) { toast({ variant: "destructive", title: "Passwords don't match" }); return; }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw });
      if (error) throw error;
      if (userId) {
        await supabase.from("profiles").update({
          must_change_password: false,
          first_login_at: new Date().toISOString(),
        }).eq("id", userId);
      }
      toast({ title: "Password updated" });
      setOpen(false);
      setShowWelcome(true);
    } catch (e: any) {
      toast({ variant: "destructive", title: "Failed", description: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => { /* force */ }}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Set a new password</DialogTitle>
            <DialogDescription>For security, choose a personal password before continuing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="np">New password</Label>
              <Input id="np" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
            </div>
            <div>
              <Label htmlFor="np2">Confirm password</Label>
              <Input id="np2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={submitting} className="w-full">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>👋 Welcome to the team!</DialogTitle>
            <DialogDescription>
              You're all set up. Use the sidebar to access the tools assigned to your role. Reach out to a Super Admin if you need additional permissions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowWelcome(false)} className="w-full">Get started</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ForcePasswordChange;
